/**
 * Deterministic palette extraction from logo pixels (spec §7). Pure — takes
 * already-decoded pixels (RGBA typed array from a Canvas, or an RGB[] list) so
 * this stays browser/Node/Deno-agnostic. The DOM decode (drawImage →
 * getImageData) lives in the caller. $0, no LLM.
 */

import { rgbToHsl, withSaturation } from './convert.js';
import type { ExtractedPalette, RGB } from './types.js';

export interface ExtractOptions {
  /** Pixels with alpha below this (0–255) are ignored. Default 8. */
  alphaThreshold?: number;
  /** Quantization bits per channel; higher = finer buckets. Default 4 (16 levels). */
  bits?: number;
  /** Saturation floor for a bucket to count as "chromatic" (brand color). Default 0.15. */
  chromaticSat?: number;
}

interface Bucket {
  count: number;
  rSum: number;
  gSum: number;
  bSum: number;
}

interface Candidate {
  color: RGB;
  count: number;
  hsl: { h: number; s: number; l: number };
}

function* iteratePixels(
  pixels: Uint8ClampedArray | Uint8Array | ReadonlyArray<RGB> | ReadonlyArray<number>,
): Generator<{ r: number; g: number; b: number; a: number }> {
  if (ArrayBuffer.isView(pixels)) {
    // isView narrows the union to the two typed-array members (arrays are not views).
    const buf = pixels;
    for (let i = 0; i + 3 < buf.length; i += 4) {
      yield { r: buf[i]!, g: buf[i + 1]!, b: buf[i + 2]!, a: buf[i + 3]! };
    }
    return;
  }
  const arr = pixels as ReadonlyArray<RGB | number>;
  if (arr.length > 0 && typeof arr[0] === 'object') {
    for (const p of arr as ReadonlyArray<RGB>) {
      yield { r: p.r, g: p.g, b: p.b, a: 255 };
    }
    return;
  }
  const nums = arr as ReadonlyArray<number>;
  for (let i = 0; i + 3 < nums.length; i += 4) {
    yield { r: nums[i]!, g: nums[i + 1]!, b: nums[i + 2]!, a: nums[i + 3]! };
  }
}

function hueDistance(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

export function extractPalette(
  pixels: Uint8ClampedArray | Uint8Array | ReadonlyArray<RGB> | ReadonlyArray<number>,
  opts: ExtractOptions = {},
): ExtractedPalette {
  const alphaThreshold = opts.alphaThreshold ?? 8;
  const bits = opts.bits ?? 4;
  const chromaticSat = opts.chromaticSat ?? 0.15;
  const shift = 8 - bits;

  const buckets = new Map<number, Bucket>();
  let considered = 0;

  for (const { r, g, b, a } of iteratePixels(pixels)) {
    if (a < alphaThreshold) continue;
    considered++;
    const key = ((r >> shift) << (bits * 2)) | ((g >> shift) << bits) | (b >> shift);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.count++;
      bucket.rSum += r;
      bucket.gSum += g;
      bucket.bSum += b;
    } else {
      buckets.set(key, { count: 1, rSum: r, gSum: g, bSum: b });
    }
  }

  if (considered === 0) {
    throw new Error('extractPalette: no opaque pixels to analyze');
  }

  const candidates: Candidate[] = [...buckets.values()].map((bk) => {
    const color: RGB = {
      r: Math.round(bk.rSum / bk.count),
      g: Math.round(bk.gSum / bk.count),
      b: Math.round(bk.bSum / bk.count),
    };
    return { color, count: bk.count, hsl: rgbToHsl(color) };
  });

  // Chromatic candidates = plausible brand colors (saturated, not near-white/black),
  // ranked by population.
  const chromatic = candidates
    .filter((c) => c.hsl.s >= chromaticSat && c.hsl.l > 0.1 && c.hsl.l < 0.92)
    .sort((a, b) => b.count - a.count);

  const chromaticWeight = chromatic.reduce((sum, c) => sum + c.count, 0);
  const isMonochrome = chromaticWeight < considered * 0.02;

  const byCount = [...candidates].sort((a, b) => b.count - a.count);

  const dominant: RGB = (isMonochrome ? byCount[0] : chromatic[0])?.color ??
    byCount[0]?.color ?? { r: 0, g: 0, b: 0 };

  // Accent = the next chromatic color whose hue is clearly distinct from dominant.
  const dominantHue = rgbToHsl(dominant).h;
  const accentCandidate =
    chromatic.find((c) => hueDistance(c.hsl.h, dominantHue) > 30) ?? chromatic[1] ?? chromatic[0];
  const accent: RGB = accentCandidate?.color ?? withSaturation(dominant, 0.55);

  // Muted = the most-common low-saturation color (a gray in the logo), else a
  // desaturated dominant.
  const mutedCandidate = byCount.find((c) => c.hsl.s < 0.2);
  const muted: RGB = mutedCandidate?.color ?? withSaturation(dominant, 0.15);

  return {
    dominant,
    accent,
    muted,
    all: byCount.map((c) => ({ color: c.color, weight: c.count / considered })),
    isMonochrome,
  };
}
